<template>
  <div>
    <MenuContentContainer
      :header="$t('edit.font')"
      border-full
      :padding-left-right="12"
    >
      <template #header-action>
        <HeaderAction
          :title="
            moreOptions ? $t('edit.less_options') : $t('edit.more_options')
          "
          @button-click="moreOptions = !moreOptions"
        >
        </HeaderAction>
      </template>
      <ContentRow class="!gap-x-2">
        <FontFamily :selected-font="fontFamily" @font-changed="changeFont" />
        <FontWeight
          :selected-weight="fontWeight"
          @weight-changed="changeWeight"
        />
      </ContentRow>
      <ContentRow block>
        <FontSize
          :selected-size="size"
          :label="$t('edit.font_size')"
          flex
          @size-changed="changeSize"
        />
      </ContentRow>
      <ContentRow block>
        <ColorPicker
          :label="$t('edit.color')"
          :default-color="color"
          position="right"
          flex
          medium
          @color-change="changeColor"
        />
      </ContentRow>
      <ContentRow block>
        <TButtonGroup :label="$t('edit.text_align')" flex>
          <TGroupButton
            data="left"
            data-testid="left"
            :active="textAlighn === 'left'"
            @button-clicked="changeAlign('left')"
          >
            <TextLeft />
          </TGroupButton>
          <TGroupButton
            data="center"
            data-testid="center"
            :active="textAlighn === 'center'"
            @button-clicked="changeAlign('center')"
          >
            <TextCenter />
          </TGroupButton>
          <TGroupButton
            data="right"
            data-testid="right"
            :active="textAlighn === 'right'"
            @button-clicked="changeAlign('right')"
          >
            <TextRight />
          </TGroupButton>
        </TButtonGroup>
      </ContentRow>
      <div v-if="moreOptions">
        <ContentRow block>
          <TInputNumber
            :label="$t('edit.line_height')"
            :model-value="lineHeight"
            :min="1.0"
            :max="4.0"
            unit=""
            step="0.01"
            regex-pattern="[0,9]{3,4}"
            flex
            :controls="false"
            @update:model-value="changeLineHeight"
          />
        </ContentRow>
        <ContentRow block>
          <TInputNumber
            :label="$t('edit.letter_spacing')"
            :model-value="letterSpasing"
            :min="0"
            :max="50"
            unit=""
            flex
            :controls="false"
            @update:model-value="changeLetterSpacing"
          />
        </ContentRow>
        <ContentRow block>
          <TButtonGroup :label="$t('edit.text_style')" flex>
            <TGroupButton
              data="italic"
              small
              toggle
              :active="fontStyle === 'italic'"
              @button-clicked="changeStyle()"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                viewBox="0 0 384 512"
              >
                <path
                  d="M128 64c0-17.7 14.3-32 32-32H352c17.7 0 32 14.3 32 32s-14.3 32-32 32H293.3L160 416h64c17.7 0 32 14.3 32 32s-14.3 32-32 32H32c-17.7 0-32-14.3-32-32s14.3-32 32-32H90.7L224 96H160c-17.7 0-32-14.3-32-32z"
                  fill="currentColor"
                />
              </svg>
            </TGroupButton>
            <TGroupButton
              data="underline"
              small
              toggle
              :active="underline"
              @button-clicked="changeUnderline()"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                viewBox="0 0 448 512"
              >
                <path
                  d="M16 64c0-17.7 14.3-32 32-32h96c17.7 0 32 14.3 32 32s-14.3 32-32 32H128V224c0 53 43 96 96 96s96-43 96-96V96H304c-17.7 0-32-14.3-32-32s14.3-32 32-32h96c17.7 0 32 14.3 32 32s-14.3 32-32 32H384V224c0 88.4-71.6 160-160 160s-160-71.6-160-160V96H48C30.3 96 16 81.7 16 64zM0 448c0-17.7 14.3-32 32-32H416c17.7 0 32 14.3 32 32s-14.3 32-32 32H32c-17.7 0-32-14.3-32-32z"
                  fill="currentColor"
                />
              </svg>
            </TGroupButton>
            <TGroupButton
              data="line-through"
              small
              toggle
              :active="lineThrough"
              @button-clicked="changeLineThrough()"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                viewBox="0 0 512 512"
              >
                <path
                  d="M161.3 144c3.2-17.2 14-30.1 33.7-38.6c21.1-9 51.8-12.3 88.6-6.5c11.9 1.9 48.8 9.1 60.1 12c17.1 4.5 34.6-5.6 39.2-22.7s-5.6-34.6-22.7-39.2c-14.3-3.8-53.6-11.4-66.6-13.4c-44.7-7-88.3-4.2-123.7 10.9c-36.5 15.6-64.4 44.8-71.8 87.3c-.1 .6-.2 1.1-.2 1.7c-2.8 23.9 .5 45.6 10.1 64.6c4.5 9 10.2 16.9 16.7 23.9H32c-17.7 0-32 14.3-32 32s14.3 32 32 32H480c17.7 0 32-14.3 32-32s-14.3-32-32-32H270.1c-.1 0-.3-.1-.4-.1l-1.1-.3c-36-10.8-65.2-19.6-85.2-33.1c-9.3-6.3-15-12.6-18.2-19.1c-3.1-6.1-5.2-14.6-3.8-27.4zM348.9 337.2c2.7 6.5 4.4 15.8 1.9 30.1c-3 17.6-13.8 30.8-33.9 39.4c-21.1 9-51.7 12.3-88.5 6.5c-18-2.9-49.1-13.5-74.4-22.1c-5.6-1.9-11-3.7-15.9-5.4c-16.8-5.6-34.9 3.5-40.5 20.3s3.5 34.9 20.3 40.5c3.6 1.2 7.9 2.7 12.7 4.3l0 0 0 0c24.9 8.5 63.6 21.7 87.6 25.6l0 0 .2 0c44.7 7 88.3 4.2 123.7-10.9c36.5-15.6 64.4-44.8 71.8-87.3c3.6-21 2.7-40.4-3.1-58.1H335.1c7 5.6 11.4 11.2 13.9 17.2z"
                  fill="currentColor"
                />
              </svg>
            </TGroupButton>
          </TButtonGroup>
        </ContentRow>
      </div>
    </MenuContentContainer>
    <MenuContentContainer
      :padding-left-right="12"
      :border-bottom="false"
      :header="$t('edit.effects')"
    ></MenuContentContainer>
    <MenuContentContainer :padding-left-right="12" :border-bottom="false">
      <div class="space-y-2">
        <TTabsToggle :header="$t('edit.outline')" no-move>
          <template #icon>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M12.8217 16.8787C12.8158 16.8646 12.81 16.8505 12.8045 16.8363L12.4325 15.8773H8.04275L7.66624 16.869C7.36034 17.674 7.02798 18.426 6.65151 18.9225L6.63877 18.9391C5.99366 19.7681 5.04942 20 4.2585 20C3.39402 20 2.62303 19.6728 2.01037 19.0999C1.40898 18.5375 1.00006 17.7753 1.00006 16.8602C1.00006 16.4494 1.07011 16.0514 1.19082 15.6725C1.28582 15.3744 1.4285 15.0011 1.60474 14.5725L5.80378 4.11776L5.80477 4.11527C5.92313 3.8178 6.06398 3.46155 6.22743 3.04621L6.23318 3.0318C6.43362 2.53608 6.66299 2.07413 6.93229 1.67268C6.93794 1.66425 6.94368 1.65587 6.94949 1.64754C7.29346 1.15449 7.73716 0.761192 8.25851 0.473312C8.87768 0.127915 9.55768 0 10.222 0C10.8808 0 11.5649 0.123036 12.185 0.472903C12.6997 0.75294 13.1472 1.13703 13.4908 1.63112C13.7552 2.002 13.9735 2.40036 14.146 2.8231C14.2905 3.15513 14.463 3.58088 14.6615 4.09255L18.9447 14.4684C19.2782 15.2542 19.5785 16.1073 19.5785 16.8382C19.5785 17.7489 19.1678 18.5015 18.5968 19.0615C17.9645 19.6816 17.1615 20 16.2864 20C15.8333 20 15.3459 19.9188 14.8811 19.6875C14.4891 19.4991 14.1412 19.2321 13.8577 18.8886C13.8497 18.8788 13.8417 18.869 13.8339 18.8591C13.5889 18.5482 13.3867 18.1709 13.2161 17.8029C13.2097 17.789 13.2035 17.7751 13.1975 17.7611C13.0541 17.428 12.9287 17.1337 12.8217 16.8787ZM15.6139 18.1904C15.8007 18.2857 16.0249 18.3333 16.2864 18.3333C16.7348 18.3333 17.116 18.1794 17.4298 17.8716C17.7512 17.5564 17.9118 17.212 17.9118 16.8382C17.9118 16.479 17.7437 15.9037 17.4074 15.1121L13.1141 4.71196C12.9123 4.19159 12.7442 3.77748 12.6097 3.46966C12.4826 3.1545 12.322 2.86133 12.1277 2.59015C11.9408 2.31897 11.6905 2.09909 11.3766 1.93052C11.0702 1.75462 10.6854 1.66667 10.222 1.66667C9.76617 1.66667 9.3813 1.75462 9.06743 1.93052C8.76104 2.09909 8.51069 2.32263 8.31639 2.60114C8.12956 2.87965 7.95021 3.23146 7.77832 3.65655C7.61392 4.07432 7.47193 4.43345 7.35236 4.73395L3.14874 15.2001C2.97686 15.6179 2.85355 15.944 2.77882 16.1785C2.70409 16.4131 2.66673 16.6403 2.66673 16.8602C2.66673 17.2413 2.8274 17.5821 3.14874 17.8826C3.47008 18.1831 3.84 18.3333 4.2585 18.3333C4.75172 18.3333 5.10669 18.1941 5.32341 17.9156C5.54013 17.6297 5.80169 17.0837 6.10809 16.2775L6.89276 14.2106H13.5737L14.3584 16.2335C14.463 16.4827 14.5863 16.7722 14.7283 17.102C14.8778 17.4245 15.016 17.6664 15.1431 17.8276C15.2701 17.9815 15.427 18.1025 15.6139 18.1904ZM10.1996 5.10774L12.6769 11.759H7.76711L10.1996 5.10774Z"
                fill="currentColor"
              />
            </svg>
          </template>
          <MenuContentContainer :padding-left-right="12">
            <ContentRow block>
              <TSliderV2
                :label="$t('edit.outline_width')"
                :model-value="outlineWidth"
                :max="30"
                unit="px"
                :reset-value="0"
                :width="184"
                input
                @update:model-value="changeOutlineWidth"
              />
            </ContentRow>
            <ContentRow block>
              <color-picker
                :label="$t('edit.outline_color')"
                color-picker-top
                flex
                medium
                :default-color="outlineColor"
                @color-change="changeOutlineColor"
              ></color-picker>
            </ContentRow>
          </MenuContentContainer>
        </TTabsToggle>
        <TTabsToggle :header="$t('edit.shadow')" no-move>
          <template #icon
            ><svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clip-path="url(#clip0_5859_8941)">
                <path
                  d="M11.2502 19.9999H5.50325C4.67371 19.9999 4.07935 19.8161 3.72016 19.4483C3.36953 19.072 3.19421 18.4777 3.19421 17.6652V3.52879C3.19421 2.69924 3.37381 2.10488 3.73299 1.7457C4.10072 1.37796 4.69081 1.19409 5.50325 1.19409H11.5965C12.4945 1.19409 13.2727 1.24968 13.9312 1.36086C14.5897 1.47203 15.1798 1.68583 15.7015 2.00226C16.1462 2.26737 16.5396 2.60517 16.8817 3.01567C17.2238 3.41761 17.4846 3.86659 17.6642 4.3626C17.8438 4.85007 17.9336 5.36746 17.9336 5.91479C17.9336 7.79623 16.9929 9.1731 15.1114 10.0454C17.5829 10.8322 18.8187 12.363 18.8187 14.6378C18.8187 15.6897 18.5493 16.639 18.0105 17.4856C17.4718 18.3237 16.7448 18.9438 15.8298 19.3457C15.2568 19.5851 14.5983 19.7562 13.8543 19.8588C13.1102 19.9529 12.2422 19.9999 11.2502 19.9999ZM10.968 11.6489H7.00413V17.1393H11.0963C13.6704 17.1393 14.9575 16.2114 14.9575 14.3556C14.9575 13.4063 14.624 12.7179 13.9569 12.2903C13.2898 11.8627 12.2935 11.6489 10.968 11.6489ZM7.00413 4.05473V8.91654H10.4933C11.4426 8.91654 12.1738 8.82674 12.6869 8.64715C13.2086 8.46756 13.6063 8.12548 13.8799 7.62091C14.0937 7.26173 14.2006 6.85979 14.2006 6.41508C14.2006 5.46581 13.8628 4.83724 13.1872 4.52937C12.5116 4.21294 11.4811 4.05473 10.0957 4.05473H7.00413Z"
                  fill="var(--c-g200)"
                />
                <path
                  d="M9.54038 17.8058H4.16125C3.3848 17.8058 2.82848 17.6317 2.49229 17.2836C2.1641 16.9273 2 16.3645 2 15.5953V2.21055C2 1.42511 2.1681 0.862356 2.50429 0.522272C2.84849 0.174091 3.40081 0 4.16125 0H9.86457C10.7051 0 11.4335 0.0526321 12.0498 0.157896C12.6662 0.26316 13.2185 0.465591 13.7068 0.765189C14.123 1.0162 14.4913 1.33604 14.8114 1.72471C15.1316 2.10528 15.3758 2.53039 15.5439 3.00003C15.712 3.46157 15.796 3.95145 15.796 4.46968C15.796 6.25107 14.9155 7.55472 13.1545 8.38064C15.4678 9.12559 16.6245 10.575 16.6245 12.7289C16.6245 13.7248 16.3723 14.6236 15.8681 15.4252C15.3638 16.2188 14.6834 16.8058 13.8269 17.1864C13.2906 17.4131 12.6742 17.5751 11.9778 17.6722C11.2814 17.7613 10.4689 17.8058 9.54038 17.8058ZM9.27622 9.89888H5.56607V15.0973H9.39629C11.8057 15.0973 13.0104 14.2188 13.0104 12.4617C13.0104 11.5629 12.6982 10.911 12.0738 10.5062C11.4495 10.1013 10.5169 9.89888 9.27622 9.89888ZM5.56607 2.70853V7.31181H8.83197C9.72048 7.31181 10.4049 7.22679 10.8852 7.05674C11.3734 6.8867 11.7457 6.56281 12.0018 6.08508C12.2019 5.74499 12.302 5.36442 12.302 4.94337C12.302 4.04457 11.9858 3.44942 11.3534 3.15792C10.7211 2.85833 9.7565 2.70853 8.45975 2.70853H5.56607Z"
                  fill="currentColor"
                />
              </g>
              <defs>
                <clipPath id="clip0_5859_8941">
                  <rect width="20" height="19.9999" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </template>
          <MenuContentContainer :padding-left-right="12">
            <ContentRow block>
              <TSlider
                :label="$t('edit.shadow_alpha')"
                :model-value="shadowAlpha"
                :max="100"
                unit="%"
                @update:model-value="changeShadow({ alpha: $event })"
              />
            </ContentRow>
            <ContentRow block>
              <TSlider
                :label="$t('edit.shadow_blur')"
                :model-value="shadowBlur"
                :max="40"
                :marks="[0, 40]"
                unit=""
                @update:model-value="changeShadow({ blur: $event })"
              />
            </ContentRow>
            <ContentRow block>
              <TSlider
                :label="$t('edit.shadow_offset_x')"
                :model-value="shadowOffsetX"
                :min="-size * 2"
                :max="size * 2"
                unit=""
                :process="(dotsPos: any) => [[50, dotsPos[0]]]"
                @update:model-value="changeShadow({ offsetX: $event })"
              />
            </ContentRow>
            <ContentRow block>
              <TSlider
                :label="$t('edit.shadow_offset_y')"
                :model-value="shadowOffsetY"
                :min="-size * 2"
                :max="size * 2"
                unit=""
                :process="(dotsPos: any) => [[50, dotsPos[0]]]"
                @update:model-value="changeShadow({ offsetY: $event })"
              />
            </ContentRow>
            <ContentRow block>
              <color-picker
                :label="$t('edit.shadow_color')"
                color-picker-top
                flex
                medium
                :default-color="shadowColor"
                @color-change="changeShadow({ color: $event })"
              ></color-picker>
            </ContentRow>
          </MenuContentContainer>
        </TTabsToggle>
      </div>
    </MenuContentContainer>
  </div>
</template>

<script lang="ts" setup>
  import ColorPicker from "@/ui/TColorPicker/colorPicker.vue";
  import TInputNumber from "@/ui/TInputNumber/TInputNumber.vue";
  import TButtonGroup from "@/ui/TButtonGroup/TButtonGroup.vue";
  import TGroupButton from "@/ui/TButtonGroup/_internal/TButton.vue";
  import TTabsToggle from "@/ui/TTabsToggle/TTabsToggle.vue";
  import TSlider from "@/ui/TSlider/TSlider.vue";
  import TSliderV2 from "@/ui/TSliderV2/TSliderV2.vue";
  import useText from "./useText/useText";

  const props = defineProps<{
    canvas: Ref<fabric.Canvas>;
    activeObject: fabric.Textbox | fabric.IText;
  }>();
  const emits = defineEmits(["update"]);

  const moreOptions = ref(false);

  const {
    fontFamily,
    color,
    size,
    textAlighn,
    fontWeight,
    fontStyle,
    underline,
    lineThrough,
    lineHeight,
    letterSpasing,
    outlineColor,
    outlineWidth,
    shadowAlpha,
    shadowBlur,
    shadowColor,
    shadowOffsetX,
    shadowOffsetY,
    changeFont,
    changeColor,
    changeSize,
    changeAlign,
    changeWeight,
    changeLetterSpacing,
    changeLineHeight,
    changeStyle,
    changeUnderline,
    changeLineThrough,
    changeOutlineColor,
    changeOutlineWidth,
    changeShadow,
    updateValues,
  } = useText(props.canvas, props.activeObject, "layers");

  onMounted(() => {
    window.$event.on("updatePanelValues", () => {
      updateValues();
      emits("update");
    });
  });

  onUnmounted(() => {
    window.$event.off("updatePanelValues");
  });
</script>
